import { ReactElement, ReactNode, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import CardGrid from '@/components/app/cards/CardGrid';
import { isProgramOrGuide, Pack, RenderablePackItem } from '@/components/app/cards/PacksList/types';
import { useAnalytics } from '@/hooks/analytics';

import messages from '../messages';
import { GridSearchResultsStyled, NoResults, RegularText } from './styles';

interface Props {
	pack?: Pack;
	packItems?: RenderablePackItem[];
	query: string;
	category: string[];
	duration: string[];
	query_id?: string;
	search_index?: string;
	usedFallback: boolean;
	message?: string;
}
interface SearchProps {
	query_id?: string;
	search_index?: string;
	filter_category: string[];
	filter_duration: string[];
	query: string;
	location: 'app' | 'meeting experience';
}

const SearchResults: React.FC<Props> = ({
	pack,
	packItems,
	query,
	category,
	duration,
	query_id,
	search_index,
	usedFallback,
	message,
}) => {
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();
	const location = 'app';
	const searchQueryProps: SearchProps = {
		query_id,
		search_index,
		filter_category: category,
		filter_duration: duration,
		query,
		location,
	};

	// SearchBar keeps this component mounted across filter changes — only
	// retyping the query unmounts it — so a plain mount-only log would collapse
	// several distinct filtered result sets into one event carrying the first
	// set's counts. Re-fire once per distinct result set instead.
	//
	// The key must come from the response, not from the query/category/duration
	// props: SearchBody re-renders with the new filter props one tick before
	// `keepPreviousData` swaps in the matching pack, so a props-only key would log
	// the *previous* result set under the *new* filter labels, then never log the
	// real new one because the key wouldn't change again once it did arrive.
	const loggedResultSetKeyRef = useRef<string | null>(null);
	useEffect(() => {
		if (!packItems || packItems.length === 0) return;

		// Keyed on the items themselves rather than on query_id/search_index: those identify
		// the request, so a revalidation of the same filters returns a fresh one and logs the
		// same result set twice. Item identifiers change only when the results do, and being
		// response-derived they still can't fire early on the previous pack while
		// `keepPreviousData` is serving it.
		const resultSetKey = packItems
			.map(item => (isProgramOrGuide(item) ? (item.guide_id ?? item.program_id) : item.title))
			.join('|');
		if (loggedResultSetKeyRef.current === resultSetKey) return;
		loggedResultSetKeyRef.current = resultSetKey;

		logEvent({
			eventName: 'Search : Results : Shown',
			eventProps: {
				filter_category: category,
				filter_duration: duration,
				query,
				query_character_count: query.length,
				// Server-returned count before any client-side type filtering, so
				// this lines up with mobile's pre-filter basis instead of the
				// smaller post-filter render count.
				total_search_results_returned: pack?.items?.length ?? packItems.length,
				location,
			},
		});
	}, [packItems, pack, query, category, duration, location, query_id, search_index, logEvent]);

	if (!query && category.length === 0 && duration.length === 0) {
		return null;
	}

	const formattedFilters = [...category, ...duration]
		.map((filter, index) => <b key={index}>{filter}</b>)
		.reduce((acc: ReactNode[], curr, index) => {
			if (index === 0) {
				return [curr];
			} else if (index === category.length - 1) {
				return [...acc, ' and ', curr];
			} else {
				return [...acc, ', ', curr];
			}
		}, []);

	if (!pack || !packItems?.length) {
		return (
			<NoResults>
				<RegularText>
					{formatMessage(messages.noResults, {
						query: query || '',
						formattedFilters: <>{formattedFilters}</>,
						b: (...chunks: ReactNode[]): ReactElement => <b>{chunks}</b>,
					})}
				</RegularText>
				<RegularText>{formatMessage(messages.searchSuggestion)}</RegularText>
			</NoResults>
		);
	}

	return (
		<>
			{usedFallback && (
				<NoResults>
					{message ||
						formatMessage(messages.fallBackResults, {
							query,
							formattedFilters: <>{formattedFilters}</>,
							b: (...chunks: ReactNode[]): ReactElement => <b>{chunks}</b>,
						})}
				</NoResults>
			)}

			<GridSearchResultsStyled>
				<CardGrid pack={pack} searchQuery={{ ...searchQueryProps }} />
			</GridSearchResultsStyled>
		</>
	);
};

export default SearchResults;
